import type { PostHog } from "posthog-js";

/**
 * Product analytics for the public site only (PostHog, EU, org "Hephon Commerce"). Never loaded
 * on the admin or the shopper chat: those pages carry retailers' customers' conversations, which
 * must not reach a third party.
 *
 * Loaded once the page is idle, so it never weighs on the first paint. Events go through our own
 * `/ingest` proxy (app/routes/ingest.$.ts): first-party, so blockers do not drop them.
 */

let loading: Promise<PostHog | null> | null = null;

export function loadAnalytics(): Promise<PostHog | null> {
	const key = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
	if (typeof window === "undefined" || !key) {
		return Promise.resolve(null);
	}
	loading ??= import("posthog-js").then(({ default: posthog }) => {
		posthog.init(key, {
			api_host: "/ingest",
			ui_host: "https://eu.posthog.com",
			// Pageviews on client-side navigation too, and the time spent on each page.
			capture_pageview: "history_change",
			capture_pageleave: true,
			autocapture: true,
			capture_exceptions: true,
			// No surveys on this site: don't download their script.
			disable_surveys: true,
			// Anonymous visitors cost no person profile; a demo request identifies them.
			person_profiles: "identified_only",
			persistence: "localStorage+cookie",
			session_recording: {
				maskAllInputs: true,
				// The demo store's chat (mounted inline in the hero) is a conversation: never
				// recorded. Its host carries `ph-no-capture`, the recorder's default block class,
				// which also keeps autocapture out of it; this blocks any inline chat by its own
				// element as well.
				blockSelector: "[data-shop-chat]",
			},
		});
		return posthog;
	});
	return loading;
}

/** A named step in the visitor's path: asked the store, picked a scene, played a call, requested a demo. */
export function track(event: string, properties?: Record<string, unknown>) {
	void loadAnalytics().then((posthog) => posthog?.capture(event, properties));
}

/** The visitor told us who they are (the demo form): their events join one person. */
export function identify(email: string, properties: Record<string, unknown>) {
	void loadAnalytics().then((posthog) =>
		posthog?.identify(email.toLowerCase(), { email, ...properties }),
	);
}
